import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ChannelGuideDialog, FieldHelpTooltip } from '../components/ChannelGuide';
import {
  Instagram, Youtube, Twitter, Facebook, MessageCircle, Send, Smartphone, Mail,
  User, Globe, Key, Check, X, Loader2, Eye, EyeOff, Shield, BookOpen,
  CheckCircle2, XCircle, AlertCircle, Wifi, WifiOff, RefreshCw, ExternalLink
} from 'lucide-react';

const CHANNEL_ICONS = {
  instagram: Instagram, youtube: Youtube, twitter: Twitter, facebook: Facebook,
  whatsapp: MessageCircle, telegram: Send, sms: Smartphone, email: Mail
};

const CHANNEL_COLORS = {
  instagram: '#E1306C', youtube: '#FF0033', twitter: '#A8B3BD', facebook: '#1877F2',
  whatsapp: '#22C55E', telegram: '#2AABEE', sms: '#84CC16', email: '#F59E0B'
};

const CHANNEL_NAMES = {
  instagram: 'Instagram', youtube: 'YouTube', twitter: 'X (Twitter)', facebook: 'Facebook',
  whatsapp: 'WhatsApp', telegram: 'Telegram', sms: 'SMS (Twilio)', email: 'E-posta (SMTP)',
  heygen: 'HeyGen'
};

const CHANNEL_FIELDS = {
  instagram: [
    { key: 'access_token', label: 'Access Token', type: 'password' },
    { key: 'instagram_business_account_id', label: 'Business Account ID', type: 'text' }
  ],
  youtube: [
    { key: 'api_key', label: 'API Key', type: 'password' },
    { key: 'channel_id', label: 'Channel ID', type: 'text' },
    { key: 'access_token', label: 'Access Token', type: 'password' },
    { key: 'refresh_token', label: 'Refresh Token', type: 'password' },
    { key: 'client_id', label: 'Client ID', type: 'text' },
    { key: 'client_secret', label: 'Client Secret', type: 'password' }
  ],
  twitter: [
    { key: 'api_key', label: 'API Key (Consumer Key)', type: 'password' },
    { key: 'api_secret', label: 'API Secret (Consumer Secret)', type: 'password' },
    { key: 'access_token', label: 'Access Token', type: 'password' },
    { key: 'access_token_secret', label: 'Access Token Secret', type: 'password' }
  ],
  facebook: [
    { key: 'access_token', label: 'Page Access Token', type: 'password' },
    { key: 'page_id', label: 'Page ID', type: 'text' }
  ],
  whatsapp: [
    { key: 'access_token', label: 'Access Token', type: 'password' },
    { key: 'phone_number_id', label: 'Phone Number ID', type: 'text' }
  ],
  telegram: [
    { key: 'bot_token', label: 'Bot Token', type: 'password' },
    { key: 'chat_id', label: 'Chat ID', type: 'text' }
  ],
  sms: [
    { key: 'account_sid', label: 'Account SID', type: 'text' },
    { key: 'auth_token', label: 'Auth Token', type: 'password' },
    { key: 'from_number', label: 'From Number', type: 'text' }
  ],
  email: [
    { key: 'smtp_host', label: 'SMTP Host', type: 'text' },
    { key: 'smtp_port', label: 'SMTP Port', type: 'text' },
    { key: 'smtp_user', label: 'SMTP User', type: 'text' },
    { key: 'smtp_password', label: 'SMTP Password', type: 'password' },
    { key: 'from_email', label: 'From Email', type: 'email' }
  ],
  heygen: [
    { key: 'api_key', label: 'HeyGen API Key', type: 'password' }
  ]
};

const LANGUAGES = [
  { code: 'tr', label: 'T\u00fcrk\u00e7e' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Fran\u00e7ais' },
  { code: 'es', label: 'Espa\u00f1ol' },
  { code: 'ar', label: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629' },
];

// Connection status component
function ConnectionStatusBadge({ status, message }) {
  if (status === 'connected') {
    return (
      <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 gap-1">
        <CheckCircle2 size={11} />
        <span>{message || 'Active'}</span>
      </Badge>
    );
  }
  if (status === 'error') {
    return (
      <Badge className="bg-red-500/10 text-red-300 border border-red-500/20 gap-1">
        <XCircle size={11} />
        <span>Error</span>
      </Badge>
    );
  }
  if (status === 'testing') {
    return (
      <Badge className="bg-sky-500/10 text-sky-300 border border-sky-500/20 gap-1 animate-pulse">
        <Loader2 size={11} className="animate-spin" />
        <span>Testing...</span>
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] border-white/20 text-gray-400 gap-1">
      <WifiOff size={11} />
      <span>Not Connected</span>
    </Badge>
  );
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { api, user, refreshUser } = useAuth();
  const [channelStatus, setChannelStatus] = useState({});
  const [activeChannel, setActiveChannel] = useState(null);
  const [channelForm, setChannelForm] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileSaving, setProfileSaving] = useState(false);

  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';

  useEffect(() => {
    api.get('/api/channels/status')
      .then(res => setChannelStatus(res.data))
      .catch(() => {});
  }, [api]);

  const handleOpenChannel = async (ch) => {
    setActiveChannel(ch);
    setValidationResult(null);
    try {
      const res = await api.get(`/api/channels/${ch}/credentials`);
      if (res.data.connected) {
        setChannelForm(res.data.credentials);
      } else {
        const fields = CHANNEL_FIELDS[ch] || [];
        const empty = {};
        fields.forEach(f => empty[f.key] = '');
        setChannelForm(empty);
      }
    } catch {
      const fields = CHANNEL_FIELDS[ch] || [];
      const empty = {};
      fields.forEach(f => empty[f.key] = '');
      setChannelForm(empty);
    }
  };

  const handleSaveChannel = async () => {
    if (!activeChannel) return;

    // Validate required fields aren't empty
    const fields = CHANNEL_FIELDS[activeChannel] || [];
    for (const field of fields) {
      const value = channelForm[field.key];
      if (!value || !String(value).trim()) {
        toast.error(lang === 'tr'
          ? `${field.label} alan\u0131 bo\u015f b\u0131rak\u0131lamaz`
          : `${field.label} is required`);
        return;
      }
    }

    setSaving(true);
    try {
      await api.post(`/api/channels/${activeChannel}/connect`, {
        channel: activeChannel,
        credentials: channelForm
      });
      toast.success(lang === 'tr' ? 'Bilgiler kaydedildi' : 'Credentials saved');
      const res = await api.get('/api/channels/status');
      setChannelStatus(res.data);
      setValidationResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleValidateChannel = async () => {
    if (!activeChannel) return;
    setValidating(true);
    setValidationResult(null);
    try {
      const res = await api.post(`/api/channels/${activeChannel}/validate`);
      setValidationResult(res.data);
      if (res.data.valid) {
        toast.success(lang === 'tr'
          ? `Ba\u011flant\u0131 ba\u015far\u0131l\u0131: ${res.data.message}`
          : `Connection successful: ${res.data.message}`);
      } else {
        toast.error(lang === 'tr'
          ? `Ba\u011flant\u0131 ba\u015far\u0131s\u0131z: ${res.data.message}`
          : `Connection failed: ${res.data.message}`);
      }
      // Refresh status
      const statusRes = await api.get('/api/channels/status');
      setChannelStatus(statusRes.data);
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'Validation failed';
      setValidationResult({ valid: false, message: errMsg });
      toast.error(errMsg);
    } finally {
      setValidating(false);
    }
  };

  const handleDisconnect = async (ch) => {
    try {
      await api.delete(`/api/channels/${ch}/disconnect`);
      toast.success(lang === 'tr' ? 'Ba\u011flant\u0131 kesildi' : 'Disconnected');
      const res = await api.get('/api/channels/status');
      setChannelStatus(res.data);
      if (activeChannel === ch) {
        setActiveChannel(null);
        setValidationResult(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.put('/api/auth/profile', { name: profileName });
      refreshUser();
      toast.success(t('common.success'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setProfileSaving(false);
    }
  };

  const allChannels = [...Object.keys(CHANNEL_FIELDS)];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        {t('settings.title')}
      </h1>

      <Tabs defaultValue="channels" className="space-y-4">
        <TabsList className="bg-[hsl(var(--secondary))]">
          <TabsTrigger value="channels" data-testid="settings-tab-channels">
            <Key size={14} className="mr-1" />
            {t('settings.channels')}
          </TabsTrigger>
          <TabsTrigger value="profile" data-testid="settings-tab-profile">
            <User size={14} className="mr-1" />
            {t('settings.profile')}
          </TabsTrigger>
          <TabsTrigger value="language" data-testid="settings-tab-language">
            <Globe size={14} className="mr-1" />
            {t('settings.language')}
          </TabsTrigger>
        </TabsList>

        {/* Channels Tab */}
        <TabsContent value="channels">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Channel List */}
            <div className="space-y-2">
              {allChannels.map(ch => {
                const Icon = CHANNEL_ICONS[ch] || Key;
                const isConnected = channelStatus[ch]?.connected;
                const isActive = activeChannel === ch;
                const validationMsg = channelStatus[ch]?.validation_message;
                return (
                  <button
                    key={ch}
                    onClick={() => handleOpenChannel(ch)}
                    data-testid={`settings-channel-${ch}`}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-l-[3px] transition-all duration-200 ${
                      isActive ? 'bg-white/10 border-white/20 shadow-lg shadow-black/20' : 'bg-[hsl(var(--card))]/80 border-white/10 hover:bg-white/5'
                    }`}
                    style={{ borderLeftColor: CHANNEL_COLORS[ch] || '#D4AF37' }}
                  >
                    <Icon size={20} style={{ color: CHANNEL_COLORS[ch] || '#D4AF37' }} />
                    <div className="flex-1 text-left">
                      <span className="text-sm font-medium block">{CHANNEL_NAMES[ch] || ch}</span>
                      {isConnected && validationMsg && (
                        <span className="text-[10px] text-[hsl(var(--muted-foreground))] block mt-0.5 truncate max-w-[200px]">
                          {validationMsg}
                        </span>
                      )}
                    </div>
                    <ConnectionStatusBadge
                      status={isConnected ? 'connected' : 'disconnected'}
                      message={isConnected ? (lang === 'tr' ? 'Aktif' : 'Active') : null}
                    />
                  </button>
                );
              })}
            </div>

            {/* Channel Form */}
            {activeChannel && (
              <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 sticky top-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      {React.createElement(CHANNEL_ICONS[activeChannel] || Key, {
                        size: 18,
                        style: { color: CHANNEL_COLORS[activeChannel] || '#D4AF37' }
                      })}
                      {CHANNEL_NAMES[activeChannel] || activeChannel}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {/* Guide Button */}
                      <ChannelGuideDialog channel={activeChannel}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 gap-1.5"
                          data-testid={`open-guide-${activeChannel}`}
                        >
                          <BookOpen size={14} />
                          <span className="text-xs">{lang === 'tr' ? 'Rehber' : 'Guide'}</span>
                        </Button>
                      </ChannelGuideDialog>
                    </div>
                  </div>

                  {/* Connection Status Indicator */}
                  {channelStatus[activeChannel]?.connected && (
                    <div className="mt-3 p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/15 flex items-center gap-2">
                      <Wifi size={14} className="text-emerald-400" />
                      <span className="text-xs text-emerald-300 flex-1">
                        {lang === 'tr' ? 'Ba\u011flant\u0131 aktif' : 'Connection active'}
                        {channelStatus[activeChannel]?.validation_message && (
                          <span className="text-emerald-400/60 ml-1">
                            \u2014 {channelStatus[activeChannel].validation_message}
                          </span>
                        )}
                      </span>
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Field inputs with help tooltips */}
                  {(CHANNEL_FIELDS[activeChannel] || []).map(field => (
                    <div key={field.key} className="space-y-1.5">
                      <label className="text-xs text-[hsl(var(--muted-foreground))] font-medium flex items-center">
                        {field.label}
                        <FieldHelpTooltip channel={activeChannel} fieldKey={field.key} />
                      </label>
                      <div className="relative">
                        <Input
                          type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                          value={channelForm[field.key] || ''}
                          onChange={e => setChannelForm({ ...channelForm, [field.key]: e.target.value })}
                          className="bg-black/20 border-white/10 font-mono text-xs pr-10 focus-visible:ring-[hsl(var(--ring))]"
                          placeholder={field.label}
                          data-testid={`channel-field-${field.key}`}
                        />
                        {field.type === 'password' && (
                          <button
                            type="button"
                            onClick={() => setShowSecrets({ ...showSecrets, [field.key]: !showSecrets[field.key] })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white transition-colors p-1"
                          >
                            {showSecrets[field.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Validation Result */}
                  {validationResult && (
                    <div className={`p-3 rounded-lg border flex items-start gap-2 ${
                      validationResult.valid
                        ? 'bg-emerald-500/5 border-emerald-500/20'
                        : 'bg-red-500/5 border-red-500/20'
                    }`}>
                      {validationResult.valid
                        ? <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                        : <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />}
                      <div>
                        <p className={`text-sm font-medium ${validationResult.valid ? 'text-emerald-300' : 'text-red-300'}`}>
                          {validationResult.valid
                            ? (lang === 'tr' ? 'Ba\u011flant\u0131 Ba\u015far\u0131l\u0131' : 'Connection Successful')
                            : (lang === 'tr' ? 'Ba\u011flant\u0131 Ba\u015far\u0131s\u0131z' : 'Connection Failed')}
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                          {validationResult.message}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 pt-2">
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveChannel}
                        disabled={saving}
                        className="flex-1 gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105"
                        data-testid="save-channel-btn"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Check size={14} className="mr-1.5" />}
                        {lang === 'tr' ? 'Kaydet ve Ba\u011fla' : 'Save & Connect'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleValidateChannel}
                        disabled={validating || !channelStatus[activeChannel]?.connected}
                        className={`border-white/10 gap-1.5 ${
                          validating ? 'animate-pulse' : ''
                        }`}
                        data-testid="test-channel-btn"
                      >
                        {validating
                          ? <Loader2 size={14} className="animate-spin" />
                          : <RefreshCw size={14} />}
                        <span className="text-xs">
                          {validating
                            ? (lang === 'tr' ? 'Test ediliyor...' : 'Testing...')
                            : (lang === 'tr' ? 'Test Et' : 'Test')}
                        </span>
                      </Button>
                    </div>
                    {channelStatus[activeChannel]?.connected && (
                      <Button
                        variant="outline"
                        onClick={() => handleDisconnect(activeChannel)}
                        className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        data-testid="disconnect-channel-btn"
                      >
                        <WifiOff size={14} className="mr-1.5" />
                        {lang === 'tr' ? 'Ba\u011flant\u0131y\u0131 Kes' : 'Disconnect'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty state when no channel selected */}
            {!activeChannel && (
              <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 flex items-center justify-center min-h-[300px]">
                <div className="text-center p-8">
                  <Key size={40} className="mx-auto mb-4 text-[hsl(var(--muted-foreground))]/30" />
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {lang === 'tr'
                      ? 'API bilgilerini g\u00f6rmek i\u00e7in soldaki listeden bir kanal se\u00e7in'
                      : 'Select a channel from the list to view API settings'}
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]/60 mt-2">
                    {lang === 'tr'
                      ? 'Her kanalda entegrasyon rehberi bulunmaktad\u0131r'
                      : 'Each channel has an integration guide available'}
                  </p>
                </div>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 max-w-lg">
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('settings.profile')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs text-[hsl(var(--muted-foreground))]">{t('auth.name')}</label>
                <Input
                  value={profileName}
                  onChange={e => setProfileName(e.target.value)}
                  className="bg-black/20 border-white/10"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-[hsl(var(--muted-foreground))]">{t('auth.email')}</label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="bg-black/20 border-white/10 opacity-60"
                />
              </div>
              <Button
                onClick={handleSaveProfile}
                disabled={profileSaving}
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                data-testid="save-profile-btn"
              >
                {profileSaving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {t('settings.save')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language">
          <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80 max-w-lg">
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t('settings.language')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {LANGUAGES.map(lng => (
                  <button
                    key={lng.code}
                    onClick={() => {
                      i18n.changeLanguage(lng.code);
                      api.put('/api/auth/profile', { language: lng.code }).catch(() => {});
                    }}
                    data-testid={`lang-${lng.code}`}
                    className={`p-3 rounded-xl border text-sm transition-all duration-200 ${
                      i18n.language?.startsWith(lng.code)
                        ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] shadow-lg shadow-[hsl(var(--primary))]/5'
                        : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                    }`}
                  >
                    {lng.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
